<template>
  <div class="categories-page">
    <section class="admin-panel categories-toolbar">
      <div>
        <p class="eyebrow">分类管理</p>
        <h2>文章分类</h2>
      </div>
      <div class="toolbar-actions">
        <el-button type="primary" @click="openCreateModal">新建分类</el-button>
      </div>
    </section>

    <section class="admin-panel categories-list-panel">
      <div class="table-status">
        <span>{{ loading ? '正在加载分类...' : `共 ${categories.length} 个分类` }}</span>
        <el-button size="mini" @click="refreshCategories">刷新</el-button>
      </div>

      <el-table
        v-loading="loading"
        class="admin-data-table"
        :data="categories"
        empty-text="暂无分类，点击上方新建分类"
        stripe
      >
        <el-table-column label="名称" min-width="160" prop="name" />
        <el-table-column label="标识 (slug)" min-width="160" prop="slug">
          <template slot-scope="{ row }"><code>{{ row.slug }}</code></template>
        </el-table-column>
        <el-table-column label="排序" prop="sortOrder" width="100" />
        <el-table-column label="文章数" prop="articleCount" width="110" />
        <el-table-column label="更新时间" prop="updatedAt" min-width="160" />
        <el-table-column label="操作" width="130">
          <template slot-scope="{ row }">
            <button class="table-action-link" type="button" @click="openEditModal(row)">编辑</button>
            <button class="table-action-danger" type="button" @click="removeCategory(row)">删除</button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <el-dialog
      :title="editingCategory ? '修改分类信息' : '创建新分类'"
      :visible.sync="showModal"
      width="460px"
      @close="closeModal"
    >
      <el-form class="admin-dialog-form" label-position="top" @submit.native.prevent="saveCategory">
        <el-form-item label="分类名称">
          <el-input v-model.trim="form.name" placeholder="例如：科技" />
        </el-form-item>

        <el-form-item label="标识 (slug)">
          <el-input v-model.trim="form.slug" placeholder="留空自动从名称生成" />
        </el-form-item>

        <el-form-item label="排序">
          <el-input-number v-model="form.sortOrder" :min="0" />
        </el-form-item>

      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeModal">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveCategory">
          {{ editingCategory ? '保存修改' : '创建分类' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchAdminCategories,
  createAdminCategory,
  updateAdminCategory,
  deleteAdminCategory,
} from '@/services/categories';
import { trackFrontendEvent } from '@/services/operationLogs';

const emptyForm = () => ({
  name: '',
  slug: '',
  sortOrder: 0,
});

export default {
  name: 'AdminCategories',
  data() {
    return {
      loading: false,
      saving: false,
      categories: [],
      showModal: false,
      editingCategory: null,
      form: emptyForm(),
    };
  },
  created() {
    this.loadCategories();
  },
  methods: {
    trackOperation(operation, detail, success = true) {
      trackFrontendEvent({
        operation,
        pagePath: this.$route.path,
        detail,
        success,
      });
    },
    normalizeCategory(item) {
      return {
        id: item.id,
        name: item.name || '',
        slug: item.slug || '',
        sortOrder: item.sortOrder != null ? item.sortOrder : 0,
        articleCount: item.articleCount != null ? item.articleCount : 0,
        createdAt: item.createdAt || '-',
        updatedAt: item.updatedAt || item.createdAt || '-',
      };
    },
    extractCategories(body) {
      const list = Array.isArray(body)
        ? body
        : body?.data?.records || body?.data?.list || body?.data || body?.records || body?.list;

      return Array.isArray(list) ? list.map(this.normalizeCategory) : [];
    },
    async loadCategories() {
      this.loading = true;

      try {
        const response = await fetchAdminCategories();
        this.categories = this.extractCategories(response.data);
      } catch (error) {
        this.categories = [];
        this.$message.error(error.response?.data?.message || '暂时无法读取分类列表');
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.trackOperation('打开新建分类弹窗', '用户点击新建分类按钮');
      this.editingCategory = null;
      this.form = emptyForm();
      this.showModal = true;
    },
    openEditModal(cat) {
      this.trackOperation('打开编辑分类弹窗', `分类ID：${cat.id || '-'}，名称：${cat.name || '-'}`);
      this.editingCategory = cat;
      this.form = {
        name: cat.name,
        slug: cat.slug,
        sortOrder: cat.sortOrder,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingCategory = null;
      this.form = emptyForm();
    },
    async saveCategory() {
      if (!this.form.name) {
        this.$message.warning('分类名称不能为空');
        return;
      }

      this.saving = true;

      try {
        const wasEditing = Boolean(this.editingCategory);
        const categoryDetail = `分类名称：${this.form.name || '-'}，slug：${this.form.slug || '-'}`;

        if (this.editingCategory) {
          await updateAdminCategory(this.editingCategory.id, this.form);
        } else {
          await createAdminCategory(this.form);
        }

        this.closeModal();
        this.trackOperation(wasEditing ? '更新分类' : '创建分类', categoryDetail);
        this.$message.success(wasEditing ? '分类已更新' : '分类已创建');
        await this.loadCategories();
      } catch (error) {
        this.trackOperation(
          this.editingCategory ? '更新分类失败' : '创建分类失败',
          `分类名称：${this.form.name || '-'}，原因：${error.response?.data?.message || error.message || '未知错误'}`,
          false,
        );
        this.$message.error(error.response?.data?.message
          || error.response?.data?.msg
          || error.message
          || '保存失败');
      } finally {
        this.saving = false;
      }
    },
    async removeCategory(cat) {
      try {
        await this.$confirm(`确定删除分类「${cat.name}」吗？`, '删除确认', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
        });
      } catch (error) {
        return;
      }

      try {
        await deleteAdminCategory(cat.id);
        this.trackOperation('删除分类', `分类ID：${cat.id || '-'}，名称：${cat.name || '-'}`);
        this.$message.success('分类已删除');
        await this.loadCategories();
      } catch (error) {
        const message = error.response?.data?.message || error.response?.data?.msg || '';
        this.trackOperation('删除分类失败', `分类ID：${cat.id || '-'}，原因：${message || '未知错误'}`, false);

        if (error.response?.status === 400) {
          this.$message.error(message || '删除失败');
          return;
        }

        this.$message.error(message || '删除失败');
      }
    },
    refreshCategories() {
      this.trackOperation('刷新分类列表', '用户点击分类列表刷新按钮');
      this.loadCategories();
    },
  },
};
</script>
